// components/dashboard/findMeATenant/FindMeATenantPaymentStep.jsx

import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Alert, CircularProgress, Paper, Divider,
} from "@mui/material";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import CreditCardIcon from "@mui/icons-material/CreditCard";

const STRIPE_SERVER_URL = process.env.REACT_APP_STRIPE_SERVER_URL;
const ORDER_AMOUNT = 395;

/**
 * FindMeATenantPaymentStep
 *
 * Saves a card on file via SetupIntent. Nothing is charged now — the $395
 * fee is collected 30 days after the order is placed (server-side billing sweep).
 *
 * Handles three card scenarios:
 *  1. Card already on file  → requiresCard: false, skip straight through
 *  2. User enters card manually via CardElement
 *  3. User uses Stripe Link / "Save with" autofill — SetupIntent is already
 *     confirmed by Stripe before our confirmCardSetup call. We detect the
 *     `setup_intent_unexpected_state` error, extract the payment_method from
 *     the returned SetupIntent, and save it normally.
 *
 * Props:
 *   email   {string}
 *   onSaved {func}   – called with { customerId, paymentMethodId? }
 *   onBack  {func}
 */
const FindMeATenantPaymentStep = ({ email, onSaved, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [customerId, setCustomerId]   = useState(null);
  const [requiresCard, setRequiresCard] = useState(true);
  const [cardComplete, setCardComplete] = useState(false);

  // ── Fetch / create a SetupIntent on mount ───────────────────────────────
  useEffect(() => {
    if (!email) return;
    setLoading(true);
    setError(null);

    fetch(`${STRIPE_SERVER_URL}/api/payments/create-setup-intent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setCustomerId(data.customerId);
        setRequiresCard(data.requiresCard);
        setClientSecret(data.clientSecret);
      })
      .catch((err) => {
        console.error("[FindMeATenantPaymentStep] setup-intent error:", err);
        setError("Failed to initialize payment. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [email]);

  // ── Save payment method to Stripe + Firestore ────────────────────────────
  const persistPaymentMethod = async (paymentMethodId) => {
    const saveRes = await fetch(
      `${STRIPE_SERVER_URL}/api/payments/save-payment-method`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId, paymentMethodId, email }),
      }
    );
    const saveData = await saveRes.json();
    if (!saveRes.ok || saveData.error) {
      throw new Error(saveData.error || "Failed to save card on file.");
    }
    return paymentMethodId;
  };

  // ── Main handler ─────────────────────────────────────────────────────────
  const handleSaveCard = async () => {
    // Scenario 1: card already on file
    if (!requiresCard) {
      onSaved?.({ customerId });
      return;
    }

    if (!stripe || !elements || !clientSecret) return;

    setSaving(true);
    setError(null);

    try {
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { email },
        },
      });

      // Scenario 3: Stripe Link / "Save with" already confirmed this SetupIntent
      // before our call. Stripe returns an error with the succeeded SetupIntent
      // attached — extract the payment_method from it and carry on.
      if (result.error) {
        const err = result.error;
        if (
          err.code === "setup_intent_unexpected_state" &&
          err.setup_intent?.status === "succeeded" &&
          err.setup_intent?.payment_method
        ) {
          console.log(
            "[FindMeATenantPaymentStep] SetupIntent already succeeded via Stripe Link — using existing payment method."
          );
          const pmId = err.setup_intent.payment_method;
          await persistPaymentMethod(pmId);
          onSaved?.({ customerId, paymentMethodId: pmId });
          return;
        }

        // Any other Stripe error — show to user
        setError(err.message || "Card could not be saved. Please try again.");
        return;
      }

      // Scenario 2: normal card entry succeeded
      const paymentMethodId = result.setupIntent.payment_method;
      await persistPaymentMethod(paymentMethodId);
      onSaved?.({ customerId, paymentMethodId });

    } catch (err) {
      console.error("[FindMeATenantPaymentStep] save card error:", err);
      setError(err.message || "Failed to save card. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Payment Method
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        We'll save your card on file. You won't be charged today — the $
        {ORDER_AMOUNT} service fee is automatically withdrawn 30 days after
        this order is placed.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : !requiresCard ? (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          You already have a card on file — no need to re-enter it.
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 1 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
            <CreditCardIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={600}>
              Card Details
            </Typography>
          </Box>
          <CardElement
            options={{ hidePostalCode: true }}
            onChange={(e) => setCardComplete(e.complete)}
          />
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="space-between">
        {onBack ? (
          <Button onClick={onBack} disabled={saving} sx={{ textTransform: "none" }}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Button
          variant="contained"
          onClick={handleSaveCard}
          disabled={loading || saving || (requiresCard && !cardComplete)}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : null}
          sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
        >
          {saving ? "Saving…" : "Save Card & Place Order"}
        </Button>
      </Box>
    </Box>
  );
};

export default FindMeATenantPaymentStep;